import type { AsyncComponentLoader, AsyncComponentOptions, Component, ComponentPublicInstance } from 'vue'

import { defineAsyncComponent, ref } from 'vue'

/** 异步组件加载错误事件 */
export const ASYNC_LOAD_ERROR_EVENT = 'async-load-error'
/** 触发异步组件加载错误事件 */
export const triggerAsyncLoadError = () => {
  window.dispatchEvent(new Event(ASYNC_LOAD_ERROR_EVENT))
}

/** 异步模块加载错误状态 */
export const asyncComponentState = {
  visible: ref(false),
}

window.addEventListener(ASYNC_LOAD_ERROR_EVENT, () => {
  asyncComponentState.visible.value = true
})

/** 使用异步组件加载错误事件 */
export const useAsyncComponentVisible = () => {
  return asyncComponentState.visible
}

/**
 * 给 defineAsyncComponent 添加错误处理
 *
 * @param source 组件加载函数或配置选项
 * @returns 配置好的异步组件
 *
 * @example
 * ```typescript
 * const MyComponent = createAsyncComponent(
 *   () => import('./MyComponent.vue')
 * )
 * ```
 */
export function createAsyncComponent<T extends Component = {
  new (): ComponentPublicInstance
}>(
  source: AsyncComponentLoader<T> | AsyncComponentOptions<T>,
) {
  if (typeof source === 'function') {
    return defineAsyncComponent({
      loader: source,
      onError(error) {
        console.error('[createAsyncComponent] 异步组件加载错误:', error)
        // 显示错误 Modal（双重保险）
        triggerAsyncLoadError()
      },
    })
  }
  return defineAsyncComponent({
    onError(error) {
      console.error('[createAsyncComponent] 异步组件加载错误:', error)
      // 显示错误 Modal（双重保险）
      triggerAsyncLoadError()
    },
    ...source, // 只处理 onError 错误处理，其他配置选项不处理
  })
}

/**
 * 带错误处理的异步模块加载函数
 *
 * @param loaderFn 模块加载函数, 如 () => import("") 语句, 立即执行
 * @returns 返回组件加载 Promise
 *
 * @example 路由声明
 * ```typescript
 * {
 *   path: "/home",
 *   component: () => createRouteComponent(() => import("@/views/Home.vue"))
 * }
 * ```
 * @example 模块加载
 * ```typescript
 * const { AES_Encrypt } = await createAsyncModule(() => import("./aes"))
 * ```
 */
export function createAsyncModule<T = any>(loaderFn: () => Promise<T>): Promise<T> {
  return loaderFn().catch(error => {
    // 显示错误 Modal
    triggerAsyncLoadError()
    console.error('[createAsyncModule] 异步模块加载错误:', error)
    // 返回一个永远不会 resolve 的 Promise，阻止路由跳转
    return new Promise(() => {})
  })
}

/** 带错误处理的路由组件加载函数, 推荐使用createAsyncModule替代 */
export const createRouteComponent = createAsyncModule
